// ./src/components/features/CreateEventWizard/steps/DrawsStep.tsx
//
// "What will you give away?" — shown when the Prize draws feature is on (ROADMAP A2b #62 P6(e)). Each round is made in
// the Prize draws page's own `DrawRoundDrawer` (prize, how many winners, tiered prizes), so the wizard cannot set a round
// up differently. The winners are DRAWN at the event, from that page — nothing here draws.
// ⓘ Before the Event page step, which offers the "Prize winners" block once a round exists.

import React, { useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useCreateGate, useGuestDraws, useToast } from '@/hooks';
import { GuestDraw, GuestDrawService } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { DrawRoundDrawer } from '@/pages/private/EventDrawsPage/DrawRoundDrawer';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

export const DrawsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { createdEventId: eventId, saving } = useEventWizard();

  const { draws, isLoaded, error, reload } = useGuestDraws(eventId);
  // Prize draws are the `engagement` bundle — GuestDrawCore refuses a create the plan does not include.
  const gate = useCreateGate(FEATURE_CODES.ENGAGEMENT, { eventId });

  const [drawer, setDrawer] = useState<{ draw: GuestDraw | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<GuestDraw | null>(null);
  const [removing, setRemoving] = useState(false);

  const sorted = useMemo(
    () => [...draws].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    [draws],
  );

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await GuestDrawService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    await reload();
  };

  if (!eventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.draws.intro', {
          defaultValue:
            'Set up the prizes you will give away. At the event you draw the winners from your guests on the Prize draws page, and can show them on the event page.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.draws.title', { defaultValue: 'Prize draws' })}
        addLabel={t('events:wizard.draws.add', { defaultValue: 'Add a prize draw' })}
        onAdd={() => gate.canCreate && setDrawer({ draw: null })}
        gate={gate}
        gateWhat={t('events:wizard.draws.another', { defaultValue: 'another prize draw' })}
        disabled={saving}
        isLoaded={isLoaded}
        readError={error}
        onRetry={() => void reload()}
        errorTitle={t('events:wizard.draws.loadFailed', { defaultValue: 'Could not load the prize draws' })}
        operation="guestDrawList"
        emptyText={t('events:wizard.draws.none', { defaultValue: 'No prize draws yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((d) => (
          <WizardItemRow
            key={d.id}
            icon="Gift"
            title={d.name ?? ''}
            subtitle={
              (d.winnersCount ?? 1) === 1
                ? t('events:wizard.draws.oneWinner', { defaultValue: 'One winner' })
                : t('events:wizard.draws.winners', { defaultValue: '{{count}} winners', count: d.winnersCount ?? 1 })
            }
            disabled={saving}
            onEdit={() => setDrawer({ draw: d })}
            onRemove={() => setPendingRemove(d)}
          />
        ))}
      </WizardListSection>

      <DrawRoundDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        draw={drawer?.draw ?? null}
        onSaved={() => {
          setDrawer(null);
          void reload();
        }}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.draws.removeTitle', { defaultValue: 'Remove this prize draw?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};
